/**
 * useMessageTemplates Hook
 * Handles all template CRUD operations via Convex
 */

import { useQuery, useMutation } from 'convex/react';
import { useCallback, useMemo } from 'react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type {
  IMessageTemplate,
  IMessageTemplateCreate,
  IMessageTemplateUpdate,
  IShareableBusiness,
  ITemplateFilters,
  IUseMessageTemplatesReturn,
} from '@/@types/messageTemplates';
import type { TemplateSortOption } from '@/constants/templateConstants';

interface IUseMessageTemplatesArgs {
  businessId: Id<'businesses'> | undefined;
  filters: ITemplateFilters;
}

export function useMessageTemplates({
  businessId,
  filters,
}: IUseMessageTemplatesArgs): IUseMessageTemplatesReturn & {
  filteredTemplates: IMessageTemplate[];
} {
  // Queries - only run when businessId is available
  const myTemplates = useQuery(
    api.messageTemplates.queries.getMyTemplates,
    businessId ? { businessId } : 'skip'
  );

  const sharedWithMe = useQuery(
    api.messageTemplates.queries.getSharedWithMe,
    businessId ? { businessId } : 'skip'
  );

  const sharedByMe = useQuery(
    api.messageTemplates.queries.getSharedByMe,
    businessId ? { businessId } : 'skip'
  );

  const userBusinesses = useQuery(
    api.messageTemplates.queries.getUserBusinesses,
    {}
  );

  // Mutations
  const createMutation = useMutation(api.messageTemplates.mutations.createTemplate);
  const updateMutation = useMutation(api.messageTemplates.mutations.updateTemplate);
  const deleteMutation = useMutation(api.messageTemplates.mutations.deleteTemplate);
  const copyMutation = useMutation(api.messageTemplates.mutations.copyTemplate);
  const incrementUsageMutation = useMutation(api.messageTemplates.mutations.incrementUsageCount);

  // Select templates based on active tab
  const baseTemplates = useMemo(() => {
    switch (filters.tab) {
      case 'my_templates':
        return myTemplates ?? [];
      case 'shared_with_me':
        return sharedWithMe ?? [];
      case 'shared_by_me':
        return sharedByMe ?? [];
      default:
        return [];
    }
  }, [filters.tab, myTemplates, sharedWithMe, sharedByMe]);

  // Apply filters and sorting
  const filteredTemplates = useMemo(() => {
    let result = [...baseTemplates];

    // Filter by channel
    if (filters.channel !== 'all') {
      result = result.filter((t) => t.channels.includes(filters.channel));
    }

    // Filter by category
    if (filters.category !== 'all') {
      result = result.filter((t) => t.category === filters.category);
    }

    // Filter by search
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(searchLower) ||
          t.content.toLowerCase().includes(searchLower)
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (filters.sort as TemplateSortOption) {
        case 'most_used':
          return b.usageCount - a.usageCount;
        case 'newest':
          return b.createdAt - a.createdAt;
        case 'alphabetical':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    return result;
  }, [baseTemplates, filters]);

  // Loading state
  const isLoading =
    myTemplates === undefined ||
    sharedWithMe === undefined ||
    sharedByMe === undefined;

  // Actions
  const createTemplate = useCallback(
    async (data: IMessageTemplateCreate): Promise<Id<'messageTemplates'>> => {
      return await createMutation(data);
    },
    [createMutation]
  );

  const updateTemplate = useCallback(
    async (data: IMessageTemplateUpdate): Promise<void> => {
      await updateMutation(data);
    },
    [updateMutation]
  );

  const deleteTemplate = useCallback(
    async (templateId: Id<'messageTemplates'>): Promise<void> => {
      await deleteMutation({ templateId });
    },
    [deleteMutation]
  );

  const copyTemplate = useCallback(
    async (templateId: Id<'messageTemplates'>): Promise<Id<'messageTemplates'>> => {
      if (!businessId) {
        throw new Error('Business ID required');
      }
      return await copyMutation({ templateId, businessId });
    },
    [copyMutation, businessId]
  );

  const incrementUsage = useCallback(
    async (templateId: Id<'messageTemplates'>): Promise<void> => {
      await incrementUsageMutation({ templateId });
    },
    [incrementUsageMutation]
  );

  return {
    templates: baseTemplates,
    filteredTemplates,
    userBusinesses: (userBusinesses ?? []) as IShareableBusiness[],
    isLoading,
    error: null,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    copyTemplate,
    incrementUsage,
  };
}
